import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle, XCircle, CircleNotch, Eye, EyeSlash, FloppyDisk } from "@phosphor-icons/react";
import axios from "axios";
import { API, errMessage } from "@/lib/api";

const client = axios.create({ baseURL: API });

const getSettings = () => client.get("/settings").then((r) => r.data);
const patchSettings = (payload) => client.patch("/settings", payload).then((r) => r.data);
const testKey = (provider) =>
  client.post(`/settings/test-key?provider=${provider}`, {}, { timeout: 30000 }).then((r) => r.data);

const GEMINI_TEXT_MODELS = [
  "gemini-2.5-flash",
  "gemini-2.5-pro",
  "gemini-3-flash",
  "gemini-3-pro",
];
const OPENAI_TEXT_MODELS = ["gpt-4o", "gpt-4o-mini", "gpt-5", "gpt-5-mini", "gpt-5.2"];
const GEMINI_IMAGE_MODELS = ["gemini-2.5-flash-image", "gemini-3-image"];
const OPENAI_IMAGE_MODELS = ["gpt-image-1"];

export const ApiKeysDialog = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null); // "gemini" | "openai" | null
  const [showG, setShowG] = useState(false);
  const [showO, setShowO] = useState(false);
  const [server, setServer] = useState(null); // masked current server state
  const [form, setForm] = useState({
    google_api_key: "", // empty = keep existing; user types new value to replace
    openai_api_key: "",
    text_provider: "gemini",
    text_model: "gemini-2.5-flash",
    image_provider: "gemini",
    image_model: "gemini-2.5-flash-image",
  });

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getSettings()
      .then((s) => {
        setServer(s);
        setForm((f) => ({
          ...f,
          google_api_key: "",
          openai_api_key: "",
          text_provider: s.text_provider,
          text_model: s.text_model,
          image_provider: s.image_provider,
          image_model: s.image_model,
        }));
      })
      .catch(() => toast.error("Ayarlar yüklenemedi"))
      .finally(() => setLoading(false));
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      // Build payload: only include keys the user typed (non-empty)
      const payload = {
        text_provider: form.text_provider,
        text_model: form.text_model,
        image_provider: form.image_provider,
        image_model: form.image_model,
      };
      if (form.google_api_key.trim()) payload.google_api_key = form.google_api_key.trim();
      if (form.openai_api_key.trim()) payload.openai_api_key = form.openai_api_key.trim();
      const updated = await patchSettings(payload);
      setServer(updated);
      setForm((f) => ({ ...f, google_api_key: "", openai_api_key: "" }));
      toast.success("API ayarları kaydedildi");
      onOpenChange(false);
    } catch (e) {
      toast.error(errMessage(e, "Kayıt başarısız"));
    } finally {
      setSaving(false);
    }
  };

  const clearKey = async (which) => {
    if (!window.confirm(`${which === "google" ? "Google" : "OpenAI"} API key silinsin mi?`))
      return;
    try {
      const payload =
        which === "google" ? { google_api_key: "" } : { openai_api_key: "" };
      const updated = await patchSettings(payload);
      setServer(updated);
      toast.success("Key silindi");
    } catch (e) {
      toast.error(errMessage(e, "Silme başarısız"));
    }
  };

  const runTest = async (provider) => {
    setTesting(provider);
    try {
      const r = await testKey(provider);
      toast.success(`${provider.toUpperCase()} bağlantısı OK · "${r.text || ""}"`);
    } catch (e) {
      toast.error(errMessage(e, "Test başarısız"));
    } finally {
      setTesting(null);
    }
  };

  const textModelOptions =
    form.text_provider === "gemini" ? GEMINI_TEXT_MODELS : OPENAI_TEXT_MODELS;
  const imageModelOptions =
    form.image_provider === "gemini" ? GEMINI_IMAGE_MODELS : OPENAI_IMAGE_MODELS;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="api-keys-dialog"
        className="bg-[#0F0F0F] border border-border rounded-none max-w-2xl p-0 gap-0 shadow-none"
      >
        <DialogHeader className="p-6 border-b border-border">
          <span className="label-mono text-primary mb-1">// api_keys</span>
          <DialogTitle className="heading-font font-black text-2xl tracking-tight text-white">
            API Keys
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground mt-1">
            Kendi Google (Gemini) ve OpenAI API keylerinizi girin. Keyler sunucuda saklanır ve
            AI üretimlerinde kullanılır.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Google Gemini */}
          <section className="border border-border p-4 bg-[#0A0A0A]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="label-mono text-primary">// google · gemini</span>
                {server?.google_api_key_set ? (
                  <span className="label-mono text-green-500 flex items-center gap-1">
                    <CheckCircle size={11} weight="fill" /> aktif
                  </span>
                ) : (
                  <span className="label-mono text-muted-foreground flex items-center gap-1">
                    <XCircle size={11} /> boş
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="test-gemini-btn"
                  onClick={() => runTest("gemini")}
                  disabled={!server?.google_api_key_set || testing !== null}
                  className="h-7 px-2 border border-border hover:border-primary hover:text-primary disabled:opacity-40 text-muted-foreground label-mono"
                >
                  {testing === "gemini" ? (
                    <CircleNotch size={11} className="animate-spin" />
                  ) : (
                    "test"
                  )}
                </button>
                {server?.google_api_key_set && (
                  <button
                    data-testid="clear-google-btn"
                    onClick={() => clearKey("google")}
                    className="h-7 px-2 border border-border hover:border-primary hover:text-primary text-muted-foreground label-mono"
                  >
                    sil
                  </button>
                )}
              </div>
            </div>

            <Label className="label-mono text-muted-foreground">API Key</Label>
            <div className="mt-2 flex items-center gap-2">
              <Input
                data-testid="google-api-key-input"
                type={showG ? "text" : "password"}
                value={form.google_api_key}
                onChange={(e) => setForm({ ...form, google_api_key: e.target.value })}
                placeholder={
                  server?.google_api_key_set
                    ? `Kayıtlı: ${server.google_api_key_masked} (değiştirmek için yeniden yaz)`
                    : "AIza…"
                }
                className="bg-background border-border rounded-none h-10 font-mono text-sm focus-visible:ring-primary focus-visible:ring-1 focus-visible:ring-offset-0"
              />
              <button
                onClick={() => setShowG((v) => !v)}
                className="h-10 w-10 flex items-center justify-center border border-border hover:border-white text-muted-foreground"
              >
                {showG ? <EyeSlash size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <p className="label-mono text-muted-foreground mt-2">
              → aistudio.google.com/apikey
            </p>
          </section>

          {/* OpenAI */}
          <section className="border border-border p-4 bg-[#0A0A0A]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="label-mono text-primary">// openai · chatgpt</span>
                {server?.openai_api_key_set ? (
                  <span className="label-mono text-green-500 flex items-center gap-1">
                    <CheckCircle size={11} weight="fill" /> aktif
                  </span>
                ) : (
                  <span className="label-mono text-muted-foreground flex items-center gap-1">
                    <XCircle size={11} /> boş
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="test-openai-btn"
                  onClick={() => runTest("openai")}
                  disabled={!server?.openai_api_key_set || testing !== null}
                  className="h-7 px-2 border border-border hover:border-primary hover:text-primary disabled:opacity-40 text-muted-foreground label-mono"
                >
                  {testing === "openai" ? (
                    <CircleNotch size={11} className="animate-spin" />
                  ) : (
                    "test"
                  )}
                </button>
                {server?.openai_api_key_set && (
                  <button
                    data-testid="clear-openai-btn"
                    onClick={() => clearKey("openai")}
                    className="h-7 px-2 border border-border hover:border-primary hover:text-primary text-muted-foreground label-mono"
                  >
                    sil
                  </button>
                )}
              </div>
            </div>

            <Label className="label-mono text-muted-foreground">API Key</Label>
            <div className="mt-2 flex items-center gap-2">
              <Input
                data-testid="openai-api-key-input"
                type={showO ? "text" : "password"}
                value={form.openai_api_key}
                onChange={(e) => setForm({ ...form, openai_api_key: e.target.value })}
                placeholder={
                  server?.openai_api_key_set
                    ? `Kayıtlı: ${server.openai_api_key_masked} (değiştirmek için yeniden yaz)`
                    : "sk-…"
                }
                className="bg-background border-border rounded-none h-10 font-mono text-sm focus-visible:ring-primary focus-visible:ring-1 focus-visible:ring-offset-0"
              />
              <button
                onClick={() => setShowO((v) => !v)}
                className="h-10 w-10 flex items-center justify-center border border-border hover:border-white text-muted-foreground"
              >
                {showO ? <EyeSlash size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <p className="label-mono text-muted-foreground mt-2">→ platform.openai.com/api-keys</p>
          </section>

          {/* Providers & Models */}
          <section className="border border-border p-4 bg-[#0A0A0A] space-y-4">
            <span className="label-mono text-primary">// model_selection</span>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-mono text-muted-foreground">Metin Sağlayıcı</Label>
                <Select
                  value={form.text_provider}
                  onValueChange={(v) =>
                    setForm({
                      ...form,
                      text_provider: v,
                      text_model:
                        v === "gemini" ? GEMINI_TEXT_MODELS[0] : OPENAI_TEXT_MODELS[0],
                    })
                  }
                >
                  <SelectTrigger
                    data-testid="text-provider-trigger"
                    className="bg-background border-border rounded-none h-10 mt-2 focus:ring-primary focus:ring-1 focus:ring-offset-0"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F0F0F] border-border rounded-none">
                    <SelectItem value="gemini" className="rounded-none font-body">
                      Gemini (Google)
                    </SelectItem>
                    <SelectItem value="openai" className="rounded-none font-body">
                      ChatGPT (OpenAI)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-mono text-muted-foreground">Metin Modeli</Label>
                <Input
                  data-testid="text-model-input"
                  list="text-model-suggest"
                  value={form.text_model}
                  onChange={(e) => setForm({ ...form, text_model: e.target.value })}
                  placeholder={textModelOptions[0]}
                  className="bg-background border-border rounded-none h-10 mt-2 font-mono text-sm focus-visible:ring-primary focus-visible:ring-1 focus-visible:ring-offset-0"
                />
                <datalist id="text-model-suggest">
                  {textModelOptions.map((m) => (
                    <option key={m} value={m} />
                  ))}
                </datalist>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-mono text-muted-foreground">Görsel Sağlayıcı</Label>
                <Select
                  value={form.image_provider}
                  onValueChange={(v) =>
                    setForm({
                      ...form,
                      image_provider: v,
                      image_model:
                        v === "gemini" ? GEMINI_IMAGE_MODELS[0] : OPENAI_IMAGE_MODELS[0],
                    })
                  }
                >
                  <SelectTrigger
                    data-testid="image-provider-trigger"
                    className="bg-background border-border rounded-none h-10 mt-2 focus:ring-primary focus:ring-1 focus:ring-offset-0"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F0F0F] border-border rounded-none">
                    <SelectItem value="gemini" className="rounded-none font-body">
                      Gemini · Nano Banana
                    </SelectItem>
                    <SelectItem value="openai" className="rounded-none font-body">
                      OpenAI · GPT Image 1
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-mono text-muted-foreground">Görsel Modeli</Label>
                <Input
                  data-testid="image-model-input"
                  list="image-model-suggest"
                  value={form.image_model}
                  onChange={(e) => setForm({ ...form, image_model: e.target.value })}
                  placeholder={imageModelOptions[0]}
                  className="bg-background border-border rounded-none h-10 mt-2 font-mono text-sm focus-visible:ring-primary focus-visible:ring-1 focus-visible:ring-offset-0"
                />
                <datalist id="image-model-suggest">
                  {imageModelOptions.map((m) => (
                    <option key={m} value={m} />
                  ))}
                </datalist>
              </div>
            </div>
          </section>
        </div>

        <DialogFooter className="p-4 border-t border-border gap-2">
          <Button
            data-testid="api-keys-cancel-btn"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-none h-10 text-muted-foreground hover:text-white hover:bg-[#1A1A1A]"
          >
            Vazgeç
          </Button>
          <Button
            data-testid="api-keys-save-btn"
            onClick={save}
            disabled={saving || loading}
            className="rounded-none h-10 bg-primary hover:bg-[#D63028] text-white font-bold uppercase tracking-wider text-xs"
          >
            <FloppyDisk size={14} weight="bold" className="mr-2" />
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApiKeysDialog;
